import { useQuery, useQueryClient } from '@tanstack/react-query';
import { billing } from '@lawbee/domain';
import { Alert, Badge, Button, ErrorState, LoadingState, Status, cn, formatDate } from '@lawbee/ui';
import { Check, ExternalLink, Lock } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { PricingCards, type PricingPlanId } from '../../components/Pricing';
import { api } from '../../lib/api';
import { useApiMutation, useSearchParam } from '../../lib/hooks';
import { can, ME_KEY, useMe } from '../../lib/me';
import type { BillingResponse, Entitlements } from '../../lib/types';
import { ReadOnlyNote } from '../app/ops/shared';
import { SettingsCard } from './layout';

const BILLING_KEY = ['billing'] as const;

const STATE_LABEL: Record<Entitlements['state'], { label: string; tone: 'done' | 'progress' | 'risk' | 'blocked' | 'idle' }> = {
  trialing: { label: 'Free trial', tone: 'progress' },
  active: { label: 'Active', tone: 'done' },
  past_due: { label: 'Payment past due', tone: 'risk' },
  inactive: { label: 'Inactive (read-only)', tone: 'blocked' },
  suspended: { label: 'Suspended', tone: 'blocked' },
};

export function BillingSettingsPage() {
  const me = useMe();
  const qc = useQueryClient();
  const checkout = useSearchParam('checkout');
  const q = useQuery({ queryKey: BILLING_KEY, queryFn: () => api.get<BillingResponse>('/api/billing') });
  const refreshed = useRef(false);

  useEffect(() => {
    if (checkout && !refreshed.current) {
      refreshed.current = true;
      void qc.invalidateQueries({ queryKey: ME_KEY });
      void qc.invalidateQueries({ queryKey: BILLING_KEY });
    }
  }, [checkout, qc]);

  if (q.isLoading) return <LoadingState label="Loading billing" />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const data = q.data;
  const e = data.entitlements;
  const state = STATE_LABEL[e.state] ?? { label: e.state, tone: 'idle' as const };
  const hive = data.managedBy === 'hive';
  const canManage = can(me, 'billing');

  return (
    <div className="space-y-6">
      {checkout && checkout !== 'cancelled' && checkout !== 'cancel' ? (
        <Alert variant="success" title="Thanks, your plan is updated">
          It can take a few seconds for the change to appear. Your features and limits update automatically.
        </Alert>
      ) : null}

      <SettingsCard
        title="Current plan"
        footer={
          hive ? (
            <Button asChild>
              <a href={data.hiveBillingUrl ?? `${(me.hiveWebUrl ?? '').replace(/\/$/, '')}/billing`} target="_blank" rel="noreferrer">
                Manage in Hive <ExternalLink />
              </a>
            </Button>
          ) : canManage && e.plan !== 'trial' ? (
            <ManageBillingButton />
          ) : undefined
        }
      >
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[22px] font-semibold tracking-tight">{e.planName}</span>
            <Status tone={state.tone}>{state.label}</Status>
            {hive ? <Badge variant="info">Managed by Hive</Badge> : null}
          </div>
          {e.state === 'trialing' && e.trialDaysLeft !== null ? (
            <p className="text-sm text-muted-foreground">
              <span className="figures font-medium text-foreground">{e.trialDaysLeft}</span> {e.trialDaysLeft === 1 ? 'day' : 'days'} left in your trial
              {e.trialEndsAt ? (
                <>
                  {' '}
                  (ends <span className="figures">{formatDate(e.trialEndsAt)}</span>)
                </>
              ) : null}
              . All Growth features are on during the trial.
            </p>
          ) : null}
          {hive ? <p className="text-sm text-muted-foreground">Your LawBee plan is part of your WRKRBZ Hive subscription. Change plans and payment details in Hive.</p> : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <UsageMeter label="Team seats" used={data.usage.seats} limit={e.limits.users} />
            <UsageMeter label="Open matters" used={data.usage.openMatters} limit={e.limits.openMatters} />
          </div>
        </div>
      </SettingsCard>

      <SettingsCard title="Included features">
        <ul className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
          {billing.PLAN_FEATURES.map((f) => {
            const on = e.features.includes(f);
            return (
              <li key={f} className={cn('flex items-center gap-2.5 text-sm', !on && 'text-muted-foreground')}>
                {on ? <Check className="size-4 shrink-0 text-success" aria-label="Included" /> : <Lock className="size-4 shrink-0" aria-label="Not included" />}
                {billing.PLAN_FEATURE_LABELS[f]}
                {!on && billing.GROWTH_ONLY_FEATURES.includes(f) ? <Badge variant="outline">Growth</Badge> : null}
              </li>
            );
          })}
        </ul>
      </SettingsCard>

      {!hive ? (
        <section aria-labelledby="plans-heading" className="space-y-4">
          <div>
            <h2 id="plans-heading" className="text-[19px] font-semibold tracking-tight">
              Plans
            </h2>
            <p className="text-sm text-muted-foreground">Prices in Canadian dollars per firm per month, plus applicable taxes.</p>
          </div>
          {!data.stripeConfigured ? <Alert variant="warning">Development mode: choosing a plan activates it immediately.</Alert> : null}
          {!canManage ? <ReadOnlyNote>Only an owner can change the plan.</ReadOnlyNote> : null}
          <PricingCards currentPlan={e.plan} action={(plan) => <PlanAction plan={plan} current={e.plan === plan && e.state !== 'trialing'} disabled={!canManage} />} />
        </section>
      ) : null}
    </div>
  );
}

function UsageMeter({ label, used, limit }: { label: string; used: number; limit: number | null }) {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const over = limit !== null && used >= limit;
  return (
    <div className="rounded-lg bg-muted/60 p-3.5">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="font-medium">{label}</span>
        <span className="figures text-muted-foreground">
          {used}
          {limit === null ? ' · unlimited' : ` of ${limit}`}
        </span>
      </div>
      {limit !== null ? (
        <div
          className="mt-2 h-1.5 overflow-hidden rounded-full bg-foreground/10"
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={limit}
          aria-valuenow={used}
        >
          <div className={cn('h-full rounded-full', over ? 'bg-warning' : 'bg-primary')} style={{ width: `${pct}%` }} />
        </div>
      ) : null}
    </div>
  );
}

function PlanAction({ plan, current, disabled }: { plan: PricingPlanId; current: boolean; disabled: boolean }) {
  const [redirecting, setRedirecting] = useState(false);
  const choose = useApiMutation((p: PricingPlanId) => api.post<{ url: string }>('/api/billing/checkout', { plan: p }), {
    onSuccess: (d) => {
      setRedirecting(true);
      window.location.assign(d.url);
    },
    errorTitle: 'Could not start checkout',
  });
  if (current) {
    return (
      <Button className="w-full" variant="secondary" disabled>
        Current plan
      </Button>
    );
  }
  return (
    <Button className="w-full" disabled={disabled} loading={choose.isPending || redirecting} onClick={() => choose.mutate(plan)}>
      Choose {billing.PLANS[plan].name.replace('LawBee ', '')}
    </Button>
  );
}

function ManageBillingButton() {
  const portal = useApiMutation(() => api.post<{ url: string }>('/api/billing/portal'), {
    onSuccess: (d) => window.location.assign(d.url),
    errorTitle: 'Could not open billing',
  });
  return (
    <Button variant="outline" loading={portal.isPending} onClick={() => portal.mutate(undefined)}>
      Manage billing <ExternalLink />
    </Button>
  );
}
